// Utility functions, enums, and helpers for FleetTracking components
// Extracted from FleetTrackingPage

import L, { LatLngExpression, LatLngBoundsExpression } from 'leaflet';
import { format, parseISO, isValid } from 'date-fns';
import { OrderInfo, Delivery } from 'types';
import { Tour, TourStatus } from 'app/models/Tour';
import React from 'react';

// Delivery status enum for deliveries
export enum DeliveryStatus {
  waitingDelivering = 0,
  loaded = 1,
  partiallyDelivered = 2,
  delivered = 3,
  notDelivered = 4,
}

// Helper functions for delivery status
export const isDelivered = (delivery: Delivery) =>
  delivery.status === DeliveryStatus.delivered;
export const isNotDelivered = (delivery: Delivery) =>
  delivery.status === DeliveryStatus.notDelivered;
export const isFinalStatus = (delivery: Delivery) =>
  isDelivered(delivery) || isNotDelivered(delivery);

// Custom colored Leaflet icons for map markers
export const blueIcon = new L.Icon({
  iconUrl:
    'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
  shadowUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
export const greenIcon = new L.Icon({
  iconUrl:
    'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
export const redIcon = new L.Icon({
  iconUrl:
    'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
export const greyIcon = new L.Icon({
  iconUrl:
    'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-grey.png',
  shadowUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

// Format a time string or timestamp to HH:mm
export const formatTime = (timeStr?: string | number) => {
  if (!timeStr) return 'N/D';
  const dateObj =
    typeof timeStr === 'string' ? parseISO(timeStr) : new Date(timeStr);
  if (!isValid(dateObj)) return 'Heure invalide';
  return format(dateObj, 'HH:mm');
};
// Format a time string or timestamp to HH:mm:ss
export const formatTimeWithSeconds = (timeStr?: string | number) => {
  if (!timeStr) return 'N/D';
  const dateObj =
    typeof timeStr === 'string' ? parseISO(timeStr) : new Date(timeStr);
  if (!isValid(dateObj)) return 'Heure invalide';
  return format(dateObj, 'HH:mm:ss');
};

// Tour status info for UI badges
export interface TourStatusInfo {
  text: string;
  variant:
    | 'default'
    | 'secondary'
    | 'destructive'
    | 'outline'
    | null
    | undefined;
  icon?: React.ReactNode;
}

// Returns a user-friendly status message and badge variant for each TourStatus value
export const getTourListStatus = (tour: Tour): TourStatusInfo => {
  const hasTerminalBackendStatus =
    tour.status === TourStatus.Cancelled || tour.status === TourStatus.Failed;

  const completedByData =
    !hasTerminalBackendStatus &&
    (tour.delivered === true ||
      (Array.isArray(tour.orders) &&
        tour.orders.length > 0 &&
        tour.orders.every(order => isFinalStatus(order as Delivery))));

  if (completedByData) {
    return { text: 'Terminée', variant: 'secondary' };
  }

  switch (tour.status) {
    case TourStatus.Created:
      return { text: 'Créée', variant: 'secondary' };
    case TourStatus.Planned:
      return { text: 'Planifiée', variant: 'default' };
    case TourStatus.InProgress:
      return { text: 'En cours', variant: 'default' };
    case TourStatus.Returning:
      return { text: 'Retour', variant: 'default' };
    case TourStatus.Stalled:
      return { text: 'Bloquée', variant: 'outline' };
    case TourStatus.Completed:
      return { text: 'Terminée', variant: 'secondary' };
    case TourStatus.Cancelled:
      return { text: 'Annulée', variant: 'destructive' };
    case TourStatus.Failed:
      return { text: 'Échouée', variant: 'destructive' };
    default:
      return { text: 'Statut inconnu', variant: 'outline' };
  }
};

// Get a display name for a delivery stop
export const getDeliveryStopName = (
  infos?: OrderInfo,
  position?: number,
): string => {
  if (!infos) return `Arrêt ${position || 'N/D'}`;
  return (
    infos.name ||
    `${infos.firstname || ''} ${infos.lastname || ''}`.trim() ||
    infos.company ||
    infos.address1 ||
    `Arrêt ${position || 'N/D'}`
  );
};
